/**
 * Copyright (c) 2022, ResMed Inc. All rights reserved.
 */

import { React, useState } from 'react';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import resmedLogo from "../../assets/resmed-logo.png";
import werewolfLogo from "../../assets/werewolf-logo.png";
import CssBaseline from '@material-ui/core/CssBaseline';
import {HelpOutline} from "@material-ui/icons";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import FormGroup from "@material-ui/core/FormGroup";
import {CloseButton} from "../common/CommonButtons";
import Modal from "@material-ui/core/Modal";
import NoteAddIcon from "@material-ui/icons/NoteAdd";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import '../../css/layout/Header.css'

export default function Header(props) {
    const [helpOpen, setHelpOpen] = useState(false);

    const handleHelpOpen = () => {
        setHelpOpen(true);
    };

    return (
        <>
            <CssBaseline />
            <AppBar
                position="fixed"
                className='app-bar'
            >
            <Toolbar className='header-toolbar'>
                <div className='logo-wrapper'>
                    <img src={resmedLogo} alt="ResMed" className="app-logo" />
                    <img src={werewolfLogo} alt="ResMed" className="app-logo" />
                </div>
                    <IconButton
                        edge="end"
                        onClick={handleHelpOpen}
                        color="primary"
                        aria-label="open drawer"
                    >
                        <HelpOutline />
                        <Typography variant="button" className='help-text' gutterBottom>
                            Help
                        </Typography>
                    </IconButton>
                </Toolbar>
            </AppBar>
            <Modal
                aria-labelledby="transition-modal-title"
                className='help-modal'
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={helpOpen}>
                    <div className='header-paper'>
                        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between"}}>
                            <>
                                <Typography variant="h5" style={{ margin: "10px" }}gutterBottom>
                                    Werewolf
                                </Typography>
                            </>
                            <FormGroup row >
                                <CloseButton handleClose={() => setHelpOpen(false)} />
                            </FormGroup>
                        </div>
                        <Typography variant="h6" style={{ margin: "10px" }}gutterBottom>
                            Data Table - Filtering & Searching
                        </Typography>
                        <Typography variant="body2" style={{ margin: "10px" }}gutterBottom>
                            The table contains both survey responses and survey schedules. You can filter by response/schedule
                            using the tabs on the top-left corner of the table. Additionally, you can filter the data based on
                            <b> sourceId</b> (Patient ECN or Device Serial #) using the "Source ID Prefix" dropdown menu. To the
                            right of this menu, there is a search bar where you can search for responses/schedules by their sourceId.
                            There are also buttons to clear the search and refresh the table.
                        </Typography>
                        <Typography variant="h6" style={{ margin: "10px" }}gutterBottom>
                            Creating a Response
                        </Typography>
                        <Typography variant="body2" style={{ margin: "10px" }}gutterBottom>
                            There is a button at the top of the table to create a survey response:
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            style={{ margin: "10px" }}
                            startIcon={<NoteAddIcon />}
                        >
                            Response
                        </Button>
                        <Typography variant="body2" style={{ margin: "10px" }}gutterBottom>
                            Clicking this will give you an option to create an object either through a Form or editing
                            JSON manually. You can also choose whether the created response will be published directly to DynamoDB,
                            or as an SQS message (to trigger the survey_response_consumer lambda workflow).
                        </Typography>
                        <Typography variant="body2" style={{ margin: "10px" }}gutterBottom>
                            If you choose to generate the object through a form (Either DynamoDB or SQS), this will open a modal containing various
                            options to create a survey response. At the top of the screen, there is a field to enter the <b>sourceId</b>. You can
                            set the prefix/type of sourceId above the text field (ex. Patient ECN/Device serial #). You can also optionally
                            generate a randomized UUID for the sourceId by clicking the button below.
                        </Typography>
                        <Typography variant="body2" style={{ margin: "10px" }}gutterBottom>
                            The <b>Questions</b> section allows you to add patient responses to various questions. Clicking on the "Add Question"
                            button will open a list of questions to choose from (ex. "How sleepy prior to therapy?"). Once you
                            select a question, an accordion menu will appear below containing options to edit the question data.
                            You can use the radio buttons to select an answer to each question. The category of the feedback (positive/negative)
                            will be determined automatically (if applicable). You can also edit the question source (MyAir or a physical device), in addition
                            to the answer date/time and day offset.
                        </Typography>
                        <Typography variant="body2" style={{ margin: "10px" }}gutterBottom>
                            The <b>Declines</b> section allows you to add instances where the patient declines to provide
                            a response. You can set the date/time and day offset for each decline and add/remove declines
                            from the list as needed.
                        </Typography>
                        <Typography variant="body2" style={{ margin: "10px" }}gutterBottom>
                            At any time, you can click the "JSON" button at the top right to get a preview of the generated
                            JSON response. Once you are ready to publish the response, click on the "Publish" button.
                            After confirming, the response will be added to either DynamoDB or SQS (depending on what you selected)
                        </Typography>
                        <Typography variant="h6" style={{ margin: "10px" }}gutterBottom>
                            Viewing, Editing & Deleting an Existing Response
                        </Typography>
                        <Typography variant="body2" style={{ margin: "10px" }}gutterBottom>
                            Every entry in the table will have 3 buttons at the right-most column.
                        </Typography>
                        <div style={{ display: 'flex', flexDirection: 'row', margin: "10px" }}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                style={{ height: '40px' }}>
                                Details
                            </Button>
                            <>
                                <IconButton
                                    variant="outlined"
                                    color="primary">
                                    <EditIcon />
                                </IconButton>
                                <IconButton color="secondary" aria-label="delete">
                                    <DeleteIcon />
                                </IconButton>
                            </>
                        </div>
                        <Typography variant="body2" style={{ margin: "10px" }}gutterBottom>
                            Clicking on the "Details" button will display the full JSON body of the survey response
                            or schedule. You'll be given options to copy the JSON (raw or pretty-printed) and export
                            as a .json or .txt file.
                        </Typography>
                        <Typography variant="body2" style={{ margin: "10px" }}gutterBottom>
                            Clicking on the "pencil" icon will open a menu to edit the response
                            (either through a form or directly editing the JSON body).
                        </Typography>
                        <Typography variant="body2" style={{ margin: "10px" }}gutterBottom>
                            Clicking on the trash can will delete the record from DynamoDB (There wil be a confirmation dialog).
                        </Typography>
                        <Typography variant="h6" style={{ margin: "10px" }}gutterBottom>
                            MQUI Tab
                        </Typography>
                        <Typography variant="body2" style={{ margin: "10px" }}gutterBottom>
                            Clicking the icon on the top-left of the screen will open a drawer menu. From there, you can
                            navigate between the Data Table and the Mobile Questions UI. The MQUI tab contains a modal with
                            the MQUI embedded within. From there, you can go through the questions & provide responses.
                        </Typography>
                    </div>
                </Fade>
            </Modal>
        </>
    )
}